import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import React, { FC, useState } from "react";
import { ResolveThunks, connect } from "react-redux";
import { css } from "styled-components";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import translate from "@insite/client-framework/Translate";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { StyledButton } from "@insite/mobius/Clickable";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getColor from "@insite/mobius/utilities/getColor";
import ADIColors from "@NA/Utils/ADIColors";
import adiRMAStatus from "@NA/Handlers/ADIRMAStatusHandler";
import Typography from "@insite/mobius/Typography";
import Link from "@insite/mobius/Link";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import CaretDown from "@NA/Icons/CaretDown";
import Hidden from "@insite/mobius/Hidden";
import ScrollToTop, { ScrollToTopStyles } from "@NA/Components/ScrollToTop";

const mapStateToProps = (state: CustomApplicationState) => ({
    isAuthenticated: state.context.session.isAuthenticated,
    location: getLocation(state),
    getRMAStatusParams: state.ADIGlobalData.ADIRMAStatusStore.loadRMAStatusParameters,
    getRMAStatusData: state.ADIGlobalData.ADIRMAStatusStore.rmaStatusResults,
    isLoading: state.ADIGlobalData.ADIRMAStatusStore.isLoading

});

const mapDispatchToProps = {
    adiRMAStatus,
};

export interface ADIRMAStatusHistoryTableStyles {
    rhHeader?: InjectableCss;
    RMAStatusTableContainer?: InjectableCss;
    dataTableStyles?: DataTableProps;
    dataTableHeadStyles?: DataTableHeadProps;
    dataTableBodyStyles?: DataTableBodyProps;
    scrollTopStyles?: ScrollToTopStyles;
    noResultFound?: InjectableCss;
}

export const adiRMAStatusHistoryTableStyles: ADIRMAStatusHistoryTableStyles = {
    rhHeader: {
        css: css`
            background: ${ADIColors.blue4};
            height: 28px;
            color: ${ADIColors.white};
            padding: 3px 5px 3px 13px;
            margin-top: 13px;
            font-size: 16px;
            font-weight: normal;
            vertical-align: middle;
        `,
    },
    dataTableBodyStyles: {
        css: css`
            background-color: transparent;
            > tr {
                &:nth-child(even) {
                    background: ${ADIColors.blue9};
                }
                background-color: transparent;
                td {
                    border-left: 1px solid transparent;
                    border-right: 1px solid ${ADIColors.gray18};
                    text-align: left;
                    display: table-cell;
                    line-height: 1.125rem;
                    color: ${getColor("text.link")};
                    font-size: 14px;
                    font-weight: 700;
                    padding: 14px 10.5px;
                    background: transparent;
                    height: auto;
                    white-space: normal;
                    > ${StyledButton} {
                        > ${TypographyStyle} {
                            &:hover {
                                color: ${getColor("text.link")};
                                text-decoration: underline;
                            }
                        }
                    }
                    &:last-child {
                        border-right: 1px solid transparent;
                    }
                    &.category-col {
                        width: 200px;
                        > ${TypographyStyle} {
                            font-size: 14px;
                            font-weight: normal;
                        }
                    }
                    &.category-col.start-date-val {
                        color: ${ADIColors.lightBlack3};
                    }
                }
            }
        `,
    },
    dataTableHeadStyles: {
        css: css`
            background: ${ADIColors.blue9};
            th {
                border-left: 1px solid transparent;
                border-right: 1px solid ${ADIColors.gray18};
                text-align: left;
                display: table-cell;
                line-height: 1.125rem;
                color: ${ADIColors.lightBlack3};
                font-size: 14px;
                font-weight: 700;
                padding: 14px 10.5px;
                background: transparent;
                height: auto;
                white-space: normal;
                &:last-child {
                    border-right: 1px solid transparent;
                }
                &.category-col {
                    width: 200px;
                    > ${TypographyStyle} {
                        font-size: 14px;
                        font-weight: normal;
                        color: ${ADIColors.tableRowBlack};
                    }
                }
                &.sort-links {
                    width: 200px;
                    font-size: 14px;
                    font-weight: normal;
                    color: ${ADIColors.lightblue4};
                    cursor: pointer;
                    svg {
                        width: 16px;
                        height: 11px;
                        padding-left: 5px;
                    }
                }
            }
        `,
    },
    dataTableStyles: {
        css: css`
            background: ${getColor("common.background")};
            margin-bottom: 1.25rem;
            table-layout: auto;
            border-spacing: 0;
        `,
    },
    RMAStatusTableContainer: {
        css: css`
            overflow: auto;
            max-width: 100%;
            flex-basis: 100%;
            > table {
                > thead {
                    background-color: transparent;
                }
            }
        `,
    },
    scrollTopStyles: {
        container: {
            css: css`
                position: fixed;
                cursor: pointer;
                bottom: 55px;
                right: 8px;
                z-index: 200;
                svg {
                    transform: scale(0.7);
                }
            `,
        },
    },
    noResultFound: {
        css: css`
            font-size: 16px;
            font-weight: bold;
            color: rgb(6, 0, 1);
        `,
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = adiRMAStatusHistoryTableStyles;

const ADIRMAStatusHistoryTable: FC<Props> = ({
    adiRMAStatus,
    isAuthenticated,
    location,
    getRMAStatusParams,
    getRMAStatusData,
    isLoading
}) => {
    const [ordersData, setOrdersData] = useState<any>([]);
    const [isMobile, setIsMobile] = React.useState(false);
    const [showScrollToTop, setShowScrollToTop] = useState(false);
    // const [sortBy, setSortBy] = React.useState("");

    React.useEffect(() => {
        const onWindowResize = () => {
            const windowWidth = window.innerWidth;
            setIsMobile(windowWidth < 700);
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, []);

    React.useEffect(() => {
        adiRMAStatus({
            ...getRMAStatusParams,
            onSuccess(result: any) {
                setOrdersData(result.result?.orders);
            },
        });
    }, []);

    React.useEffect(() => {
        if (!isAuthenticated && location.pathname.includes("MyAccount")) {
            const returnUrl = location.pathname;
            window.location.href = `/MyAccount/SignIn?returnUrl=${encodeURIComponent(returnUrl)}`;
        }
    }, []);

    const formatDate = (dateString: string) => {
        if (!dateString) return "";

        const date = new Date(dateString);

        const formattedMonth = (date.getMonth() + 1).toString().padStart(2, "0");
        const formattedDay = date.getDate().toString().padStart(2, "0");
        const formattedYear = date.getFullYear();

        return `${formattedMonth}/${formattedDay}/${formattedYear}`;
    };

    const formatTime = (timeString: string) => {
        if (!timeString) return "";

        const [time, amPm] = (timeString || "").trim().split(" ");
        const [hours, minutes, seconds] = ((time || "").split(":") || []).map(part => parseInt(part || "0", 10));
        const formattedHours = (hours % 12 || 12).toString().padStart(2, "0");
        const formattedMinutes = (minutes || 0).toString().padStart(2, "0");
        const formattedSeconds = (seconds || 0).toString().padStart(2, "0");

        return `${formattedHours}:${formattedMinutes}:${formattedSeconds} ${amPm ? amPm.toUpperCase() : ""}`;
    };

    const handleSort = (sortBy: string) => {
        let sort = sortBy;
        if (getRMAStatusParams?.sortBy === sort) {
            sort = `${sort} DESC`;
        } else {
            sort = sortBy;
        }

        adiRMAStatus({
            ...getRMAStatusParams,
            sortBy: sort,
            onSuccess() { }
        });
    };

    React.useEffect(() => {
        const toggleVisibility = () => {
            if (window.pageYOffset > 200) {
                setShowScrollToTop(true);
            } else {
                setShowScrollToTop(false);
            }
        };
        window.addEventListener("scroll", toggleVisibility);
        return () => window.removeEventListener("scroll", toggleVisibility);
    }, []);

    if (!isAuthenticated) {
        return null;
    }


    const getOrdersData = getRMAStatusData?.orders;
    if (!getOrdersData) {
        return (
            <StyledWrapper {...styles.noResultFound}>
                <Typography>{translate(`No results found.`)}</Typography>
            </StyledWrapper>
        );
    }
    if (getOrdersData.length === 0) {
        return (
            <StyledWrapper {...styles.noResultFound}>
                <Typography>{translate(`No results found.`)}</Typography>
            </StyledWrapper>
        );
    }
    const renderTrackingNumbers = (tracking: string) => {
        return tracking.split(",").map((item: any, index: number) => {
            const parts = item.split("%");
            const trackingNumber = parts[0].trim();
            if (trackingNumber) {
                return (
                    <Link
                        href={`${parts[1].trim()}`}
                        target="_blank"
                        key={index}
                    >
                        {trackingNumber}
                    </Link>
                );
            }
            return null;

        });
    };
    return (
        <>
            {getRMAStatusData && (
                <StyledWrapper {...styles.RMAStatusTableContainer}>
                    <StyledWrapper {...styles.rhHeader}>
                        <Typography>{translate(`Search History`)}</Typography>
                    </StyledWrapper>
                    <DataTable {...styles.dataTableStyles}>
                        {isMobile ? (
                            <>
                                <DataTableHead {...styles.dataTableHeadStyles}>
                                    <DataTableHeader className="sort-links" onClick={() => handleSort("PONumber")}>
                                        {translate("PO Number")}
                                        {getRMAStatusParams.sortBy?.includes("PONumber DESC") && <CaretDown />}
                                    </DataTableHeader>
                                    <DataTableHeader className="sort-links" onClick={() => handleSort("Status")}>
                                        {translate("Status")}
                                        {getRMAStatusParams.sortBy?.includes("Status DESC") && <CaretDown />}
                                    </DataTableHeader>
                                    <DataTableHeader
                                        className="sort-links"
                                        onClick={() => handleSort("Date_Submitted")}
                                    >
                                        {translate("Date Submitted")}
                                        {getRMAStatusParams.sortBy?.includes("Date_Submitted DESC") && <CaretDown />}
                                    </DataTableHeader>
                                </DataTableHead>
                                <DataTableBody {...styles.dataTableBodyStyles}>
                                    {ordersData &&
                                        ordersData?.map((ordersData: any) => (
                                            <DataTableRow key={ordersData.id}>
                                                <DataTableCell className="category-col">
                                                    <Link
                                                        href={`/MyAccount/Returns/ReturnDetail?rmaReturnID=${encodeURIComponent(
                                                            ordersData.rmaReturnID,
                                                        )}`}
                                                    >
                                                        {ordersData.customerPO}
                                                    </Link>
                                                </DataTableCell>

                                                <DataTableCell className="category-col start-date-val">
                                                    {ordersData.statusDisplay}
                                                </DataTableCell>
                                                <DataTableCell className="category-col start-date-val">
                                                    {formatDate(ordersData.orderDate)}
                                                </DataTableCell>
                                            </DataTableRow>
                                        ))}
                                </DataTableBody>
                            </>
                        ) : (
                            <>
                                <DataTableHead {...styles.dataTableHeadStyles}>
                                    <DataTableHeader className="sort-links" onClick={() => handleSort("PONumber")}>
                                        {translate("PO Number")}
                                        {getRMAStatusParams.sortBy?.includes("PONumber DESC") && <CaretDown />}
                                    </DataTableHeader>
                                    <DataTableHeader className="category-col">
                                        {translate("Tracking Number")}
                                    </DataTableHeader>

                                    <DataTableHeader className="sort-links" onClick={() => handleSort("Status")}>
                                        {translate("Status")}
                                        {getRMAStatusParams.sortBy?.includes("Status DESC") && <CaretDown />}
                                    </DataTableHeader>
                                    <DataTableHeader
                                        className="sort-links"
                                        onClick={() => handleSort("Date_Submitted")}
                                    >
                                        {translate("Date Submitted")}
                                        {getRMAStatusParams.sortBy?.includes("Date_Submitted DESC") && <CaretDown />}
                                    </DataTableHeader>
                                    <DataTableHeader className="category-col">
                                        {translate("Time Submitted")}
                                    </DataTableHeader>
                                </DataTableHead>
                                <DataTableBody {...styles.dataTableBodyStyles}>
                                    {ordersData &&
                                        ordersData?.map((ordersData: any) => (
                                            <DataTableRow key={ordersData.id}>
                                                <DataTableCell className="category-col">
                                                    <Link
                                                        href={`/MyAccount/Returns/ReturnDetail?rmaReturnID=${encodeURIComponent(
                                                            ordersData.rmaReturnID,
                                                        )}`}
                                                    >
                                                        {ordersData.customerPO}
                                                    </Link>
                                                </DataTableCell>
                                                <DataTableCell className="category-col">
                                                    {renderTrackingNumbers(ordersData.tracking)}
                                                </DataTableCell>

                                                <DataTableCell className="category-col start-date-val">
                                                    {ordersData.statusDisplay}
                                                </DataTableCell>
                                                <DataTableCell className="category-col start-date-val">
                                                    {formatDate(ordersData.orderDate)}
                                                </DataTableCell>
                                                <DataTableCell className="category-col start-date-val">
                                                    {formatTime(ordersData.orderTime)}
                                                </DataTableCell>
                                            </DataTableRow>
                                        ))}
                                </DataTableBody>
                            </>
                        )}
                        {showScrollToTop && (
                            <Hidden below="lg">
                                <ScrollToTop extendedStyles={styles.scrollTopStyles} />
                            </Hidden>
                        )}
                    </DataTable>
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ADIRMAStatusHistoryTable),
    definition: {
        displayName: "ADI RMA Status History Table",
        group: "Common",
    },
};

export default widgetModule;
